import React, { useRef } from 'react';
import { Form, useClassNameMapper } from 'react-bootstrap';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import ComponentHelper from '../../../helpers/ui/ComponentHelper/ComponentHelper';
import InfoPopover from '../../common/InfoPopover/InfoPopover';
import type { FormFieldVariant } from '../enums';
import { FormCheckInputType, CheckFieldVariant } from '../enums';
import FormField from '../FormField/FormField';
import type { FormFieldProps, FormFieldType, FormGroup } from '../types';
import { FieldType } from '../types';
import { useFormWatch } from '../useFormWatch';
import localStyles from './CheckField.module.pcss';
import type { ConfirmModalProps } from './ConfirmModal/ConfirmModal';
import ConfirmModal from './ConfirmModal/ConfirmModal';

interface DisabledFieldWatchers<DataT extends FieldValues> {
  /**
   * Field watchers that will trigger the specified callback when their value changes.
   */
  watchers: FieldPath<DataT>[];
  /**
   * The callback when a watched field changes. This will only include the watched fields.
   */
  callback: (watcherValues: Partial<DataT>, formMethods?: UseFormReturn<DataT>) => boolean;
}

/**
 * Check field specification to render the checkbox field. NameT is the name of the field and DataT is the definition
 * of bean that holds all the fields for the form.
 */
export interface CheckFieldSpec<NameT extends FieldPath<DataT>, DataT extends FieldValues>
  extends FormFieldType<NameT, DataT, FormFieldVariant.CHECK> {
  /**
   * Checkbox field input type.
   */
  inputType?: FormCheckInputType;
  /**
   * Form field form group specification to specify how react bootstrap Form.Group should render.
   */
  formGroupSpec?: Omit<FormGroup, 'label' | 'description'>;
  /**
   * Custom checkbox label to display, SettingFormLabel can be passed with
   * its properties to display a label and badge.
   */
  label?: string | React.ReactNode;
  /**
   * Help text description.
   */
  description?: string | React.ReactNode | true;
  /**
   * Callback to run on checkbox input change.
   */
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    formMethods: UseFormReturn<DataT>
  ) => void;
  /**
   * Switch variant to display it on the right hand side.
   */
  variant?: CheckFieldVariant;
  /**
   * Callback when a key is pressed within the input.
   * @callback
   */
  onKeyDown?: (event: React.KeyboardEvent, formMethods: UseFormReturn<DataT>) => void | undefined;
  /**
   * Text selection handling
   */
  textSelection?: boolean;
  /**
   * A field watcher to decide whether a field is disabled or not based on the callback.
   */
  isDisabledFieldWatchers?: DisabledFieldWatchers<DataT>;
  /**
   * Classnames applied to checkField input
   */
  checkFieldInputClassName?: string;
  /**
   * Classnames applied to checkField text
   */
  checkFieldTextClassName?: string;
  /**
   * Info text to display in the popover
   */
  infoText?: string;
  /**
   * Confirm modal to show when the checkbox is checked
   */
  confirmModal?: ConfirmModalProps;
}

/**
 * Renders a checkbox input as either a standard checkbox or a bootstrap switch
 *
 * @author Manish Shrestha, Jonathan Bridges
 */
const CheckField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  formId,
  i18n,
  className,
  formMethods,
  onFormSubmit,
  submitOnChange,
  allFields
}: FormFieldProps<NameT, DataT, CheckFieldSpec<NameT, DataT>>): React.ReactElement => {
  const cx = useClassNameMapper(localStyles);
  const {
    formState: { errors },
    control
  } = formMethods;
  const { hasMessage, formatMessage, loading: textLoading } = i18n;
  const hasBeenFocused = useRef(false);
  const {
    name,
    inputType,
    attributes,
    focus,
    focusHandler,
    disabled,
    description,
    label,
    variant,
    onChange: onChangeCallback,
    onKeyDown,
    textSelection,
    isDisabledFieldWatchers,
    checkFieldInputClassName,
    checkFieldTextClassName,
    infoText,
    confirmModal
  } = fieldSpec;
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const { watchers } = isDisabledFieldWatchers || {};
  const watchedValues = useFormWatch(watchers, control);

  if (textLoading) {
    return null;
  }

  const adjustedFieldSpec: FormFieldType<NameT, DataT, FormFieldVariant.CHECK> = {
    ...fieldSpec,
    formGroupSpec: {
      ...fieldSpec.formGroupSpec,
      label: false,
      showInfo: false
    }
  };

  /**
   * Function to check if the field should be disabled or not
   */
  function isFieldDisabled(): boolean {
    if (isDisabledFieldWatchers) {
      const { callback } = isDisabledFieldWatchers;
      if (watchers?.length > 0) {
        return callback(watchedValues, formMethods);
      }
    }
    return disabled;
  }

  const fieldKey = `${formId}.${name}`;
  const infoTextKey = `${formId}.${name}.info`;
  const showInfo = infoText || hasMessage(infoTextKey);

  const helpTextValue =
    description && (description === true ? formatMessage(`${fieldKey}.description`) : description);

  const isStretchedSwitch = variant === CheckFieldVariant.STRETCHED;
  const isInlineSwitch = !isStretchedSwitch && inputType === FormCheckInputType.SWITCH;

  return (
    <FormField
      fieldSpec={adjustedFieldSpec}
      fieldType={FieldType.UNCONTROLLED}
      formId={formId}
      i18n={i18n}
      formMethods={formMethods}
      onFormSubmit={() => {
        if (confirmModal && formMethods.getValues(name)) {
          setShowModal(true);
          confirmModal.onConfirm = () => {
            setShowModal(false);
            onFormSubmit();
          };
        } else {
          onFormSubmit();
        }
      }}
      submitOnChange={submitOnChange}
      allFormFields={allFields}
      className={cx(className)}
    >
      {({
        render: {
          register: { ref, onBlur },
          onChange
        }
      }) => {
        return (
          <>
            <Form.Check type={inputType} className={cx({ 'lia-is-stretched': isStretchedSwitch })}>
              <Form.Check.Input
                name={name}
                aria-describedby={name}
                ref={(element): void => {
                  ref(element);
                  if (focus) {
                    ComponentHelper.focusHandler(hasBeenFocused, element, focusHandler);
                  }
                }}
                isInvalid={errors[name] !== undefined}
                disabled={isFieldDisabled()}
                data-testid={`CheckField.${formId}.${name}`}
                onChange={event => {
                  if (confirmModal && event.target.checked) {
                    event.preventDefault();
                  }
                  if (onChangeCallback) {
                    onChangeCallback(event, formMethods);
                  }
                  onChange(event);
                }}
                onBlur={onBlur}
                onKeyDown={onKeyDown ? event => onKeyDown(event, formMethods) : undefined}
                className={cx(checkFieldInputClassName, {
                  'lia-switch': isInlineSwitch
                })}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...attributes}
              />
              <div className={cx(checkFieldTextClassName)}>
                <div className={cx('lia-label-wrap')}>
                  <Form.Check.Label
                    className={cx('lia-label', {
                      'lia-text-click': textSelection
                    })}
                    data-testid={`CheckField.Label.${fieldKey}`}
                  >
                    {label || formatMessage(`${fieldKey}.label`)}
                  </Form.Check.Label>
                  {showInfo && (
                    <InfoPopover
                      infoText={infoText || formatMessage(infoTextKey)}
                      fieldName={
                        typeof label === 'string'
                          ? label
                          : hasMessage(`${fieldKey}.label`)
                          ? formatMessage(`${fieldKey}.label`)
                          : undefined
                      }
                    />
                  )}
                </div>
                {helpTextValue && <Form.Text>{helpTextValue}</Form.Text>}
              </div>
            </Form.Check>
            {confirmModal && (
              <ConfirmModal
                fieldName={name}
                title={confirmModal.title}
                description={confirmModal.description}
                confirmText={confirmModal.confirmText}
                cancelText={confirmModal.cancelText}
                show={showModal}
                onCancel={() => {
                  setShowModal(false);
                }}
                onConfirm={confirmModal.onConfirm}
                formMethods={formMethods}
              />
            )}
          </>
        );
      }}
    </FormField>
  );
};
export default CheckField;
